import React, { Component } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Container,
  Radio,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Paper,
  ListItemIcon,
  CircularProgress,
  Alert,
  Avatar,
} from "@mui/material";
import {
  ArrowBackIos,
  Close,
  Moped,
  CreditCard,
  Pix,
  Payments,
  Storefront,
  CheckCircleOutline,
  Warning,
  LocalShipping,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { CartContext } from "../../comp/CartContext";
import { WithRouter } from "../../comp/WithRouter";

const STEPS = ["Entrega", "Pagamento", "Confirmação"];
const DEFAULT_COLOR = "#1976d2";
const SUCCESS_COLOR = "#512da8";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const calculateSubtotal = (cart = []) =>
  cart.reduce((acc, item) => {
    const itemBasePrice = Number(item.preco_unitario_momento || 0);
    const optionsPrice = (item.opcoes || []).reduce(
      (optAcc, opt) => optAcc + Number(opt.preco_adicional || 0),
      0
    );
    return acc + (itemBasePrice + optionsPrice) * Number(item.quantidade || 1);
  }, 0);

const calculateShippingFee = (deliveryType, storeDelivery, subtotal, selectedShippingOption) => {
  if (deliveryType === "pickup") return 0;

  if (deliveryType === "delivery") {
    if (!storeDelivery) return 0;
    const freeAbove = Number(storeDelivery.gratisAcima) || 0;
    if (freeAbove > 0 && subtotal >= freeAbove) return 0;
    return Number(storeDelivery.valoreEntrega) || 0;
  }

  if (deliveryType === "melhor_envio" && selectedShippingOption) {
    return Number(selectedShippingOption.price || 0);
  }

  return 0;
};

/* ---------------------------- Passo 1: Entrega --------------------------- */

class DeliveryStep extends Component {
  static contextType = CartContext;

  render() {
    const {
      storeDelivery,
      deliveryType,
      onSelectDeliveryType,
      selectedAddress,
      shippingFee,
      distanceKm,
      melhorEnvioOptions,
      selectedShippingOption,
      onSelectShippingOption,
    } = this.props;

    const themeColor = this.context?.data?.color || this.context?.color || DEFAULT_COLOR;

    const canDeliverLocal =
      storeDelivery?.habilitarEntrega === 1 && storeDelivery?.entregaLocal === 1;
    const canPickup = storeDelivery?.retiradaLocal === 1;
    const canMelhorEnvio =
      storeDelivery?.habilitarEntrega === 1 &&
      storeDelivery?.melhorEnvio === 1 &&
      melhorEnvioOptions?.length > 0;

    const isDistanceExceeded =
      storeDelivery?.distanciaMaxima > 0 &&
      distanceKm > storeDelivery.distanciaMaxima;

    if (!canDeliverLocal && !canPickup && !canMelhorEnvio) {
      return (
        <Alert severity="error" icon={<Warning />}>
          O estabelecimento não possui opções de entrega ou retirada disponíveis no momento.
        </Alert>
      );
    }

    return (
      <Box>
        {/* Entrega Própria da Loja */}
        {canDeliverLocal && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              cursor: isDistanceExceeded ? "not-allowed" : "pointer",
              borderColor: deliveryType === "delivery" ? themeColor : "#eee",
              opacity: isDistanceExceeded ? 0.6 : 1,
            }}
            onClick={() => {
              if (isDistanceExceeded) {
                toast.warning(
                  `Seu endereço está a ${distanceKm}km, ultrapassando o limite máximo de ${storeDelivery.distanciaMaxima}km para entregas.`
                );
                return;
              }
              onSelectDeliveryType("delivery");
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Moped sx={{ mr: 1, color: deliveryType === "delivery" ? themeColor : "#666" }} />
                <Typography sx={{ fontWeight: "bold" }}>Receber via Motoboy / Loja</Typography>
              </Box>
              <Radio checked={deliveryType === "delivery"} sx={{ color: themeColor }} />
            </Box>

            {isDistanceExceeded && (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                Endereço fora do raio de entrega ({distanceKm} km / Máx: {storeDelivery.distanciaMaxima} km).
              </Alert>
            )}

            {deliveryType === "delivery" && selectedAddress && !isDistanceExceeded && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {selectedAddress.street}, {selectedAddress.house_number}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedAddress.district}, {selectedAddress.city} - {selectedAddress.state}
                    </Typography>
                  </Box>
                  <Link to="/address" style={{ color: themeColor, fontWeight: "bold", fontSize: 12 }}>
                    Editar
                  </Link>
                </Box>
                <Box sx={{ mt: 1, display: "flex", gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Taxa de entrega</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {shippingFee === 0 ? "Grátis" : formatCurrency(shippingFee)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Tempo estimado</Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {storeDelivery.tempoMin}-{storeDelivery.tempoMax} min
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Opções de Envios (Melhor Envio: Correios/Jadlog/etc.) */}
        {canMelhorEnvio && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#333" }}>
              Entrega por Transportadora (Melhor Envio)
            </Typography>

            {selectedAddress && (
              <Box sx={{ mb: 1.5, px: 1 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Enviar para: <strong>{selectedAddress.street}, {selectedAddress.house_number}</strong> - {selectedAddress.city}/{selectedAddress.state} ({selectedAddress.zip_code})
                </Typography>
              </Box>
            )}

            {melhorEnvioOptions.map((option) => {
              const isSelected =
                deliveryType === "melhor_envio" && selectedShippingOption?.id === option.id;

              return (
                <Paper
                  key={option.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    borderColor: isSelected ? themeColor : "#eee",
                    borderWidth: isSelected ? 2 : 1,
                  }}
                  onClick={() => {
                    onSelectDeliveryType("melhor_envio");
                    onSelectShippingOption(option);
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      {option.company?.picture ? (
                        <Avatar
                          src={option.company.picture}
                          alt={option.company.name}
                          variant="square"
                          sx={{ width: 40, height: 24, objectFit: "contain" }}
                        />
                      ) : (
                        <LocalShipping sx={{ color: themeColor }} />
                      )}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {option.company?.name} - {option.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Chega em até {option.delivery_time} dias úteis
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: themeColor }}>
                        {formatCurrency(option.price)}
                      </Typography>
                      <Radio checked={isSelected} sx={{ color: themeColor }} />
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        {/* Retirada na Loja */}
        {canPickup && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              borderColor: deliveryType === "pickup" ? themeColor : "#eee",
            }}
            onClick={() => onSelectDeliveryType("pickup")}
          >
            <Storefront sx={{ mr: 1, color: deliveryType === "pickup" ? themeColor : "#666" }} />
            <Typography sx={{ flexGrow: 1, fontWeight: deliveryType === "pickup" ? "bold" : "normal" }}>
              Retirar no estabelecimento
            </Typography>
            <Radio checked={deliveryType === "pickup"} sx={{ color: themeColor }} />
          </Paper>
        )}
      </Box>
    );
  }
}

/* --------------------------- Passo 2: Pagamento --------------------------- */

class PaymentOption extends Component {
  static contextType = CartContext;

  render() {
    const { selected, onClick, icon, label, secondary, highlight } = this.props;
    const themeColor = this.context?.data?.color || this.context?.color || DEFAULT_COLOR;

    return (
      <ListItemButton
        onClick={onClick}
        sx={{
          border: selected ? `2px solid ${themeColor}` : "1px solid #eee",
          mb: 1,
          borderRadius: 2,
          bgcolor: highlight ? "#f9f9f9" : "transparent",
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} secondary={secondary} />
      </ListItemButton>
    );
  }
}

class PaymentStep extends Component {
  static contextType = CartContext;

  render() {
    const { paymentsStore, paymentMethod, onSelectPaymentMethod } = this.props;

    if (!paymentsStore) {
      return <Alert severity="error">Nenhuma forma de pagamento disponível na loja.</Alert>;
    }

    const hasOnlinePayment =
      paymentsStore.credit === 1 || paymentsStore.debit === 1 || paymentsStore.pix === 1;
    const hasOnDeliveryPayment = paymentsStore.money === 1 || Boolean(paymentsStore.c_machine);

    return (
      <Box>
        {hasOnlinePayment && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "#333" }}>
              Pagar online
            </Typography>
            <List disablePadding>
              {paymentsStore.credit === 1 && (
                <PaymentOption
                  selected={paymentMethod === "credit"}
                  onClick={() => onSelectPaymentMethod("credit")}
                  icon={<CreditCard />}
                  label="Cartão de crédito"
                />
              )}
              {paymentsStore.debit === 1 && (
                <PaymentOption
                  selected={paymentMethod === "debit"}
                  onClick={() => onSelectPaymentMethod("debit")}
                  icon={<CreditCard />}
                  label="Cartão de débito"
                />
              )}
              {paymentsStore.pix === 1 && (
                <PaymentOption
                  selected={paymentMethod === "pix"}
                  onClick={() => onSelectPaymentMethod("pix")}
                  icon={<Pix sx={{ color: "#32bcad" }} />}
                  label="Pix"
                  secondary="Aprovação instantânea"
                  highlight
                />
              )}
            </List>
          </>
        )}

        {hasOnDeliveryPayment && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: "bold", color: "#333" }}>
              Pagar na entrega / retirada
            </Typography>
            <List disablePadding>
              {paymentsStore.money === 1 && (
                <PaymentOption
                  selected={paymentMethod === "money"}
                  onClick={() => onSelectPaymentMethod("money")}
                  icon={<Payments />}
                  label="Dinheiro"
                />
              )}
              {paymentsStore.c_machine && (
                <PaymentOption
                  selected={paymentMethod === "machine"}
                  onClick={() => onSelectPaymentMethod("machine")}
                  icon={<CreditCard />}
                  label="Maquininha no local / entrega"
                />
              )}
            </List>
          </>
        )}
      </Box>
    );
  }
}

/* -------------------------- Passo 3: Confirmação -------------------------- */

class ConfirmationStep extends Component {
  static contextType = CartContext;

  render() {
    const {
      storeDelivery,
      storeAddress,
      deliveryType,
      selectedAddress,
      cart,
      subtotal,
      shippingFee,
      total,
      paymentMethod,
      selectedShippingOption,
    } = this.props;

    const themeColor = this.context?.data?.color || this.context?.color || DEFAULT_COLOR;

    const paymentLabels = {
      credit: "Cartão de crédito (Online)",
      debit: "Cartão de débito (Online)",
      pix: "Pix (Online)",
      money: "Dinheiro na entrega/retirada",
      machine: "Maquininha na entrega/retirada",
    };

    return (
      <Box>
        {/* Identificação do Estabelecimento */}
        {storeAddress && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f8f9fa", borderRadius: 1.5 }}>
            <Typography variant="caption" color="textSecondary" display="block">
              ESTABELECIMENTO VENDEDOR:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {storeAddress.rua}, {storeAddress.numero} {storeAddress.complemento || ""} - {storeAddress.bairro}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {storeAddress.cidade} - {storeAddress.estado} | CEP: {storeAddress.cep}
            </Typography>
          </Box>
        )}

        {/* Previsão de Entrega */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, textAlign: "center", bgcolor: "#fafafa" }}>
          <Typography variant="caption" color="textSecondary" display="block">
            PRAZO ESTIMADO DE {deliveryType === "pickup" ? "RETIRADA" : "ENTREGA"}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
            {deliveryType === "melhor_envio" && selectedShippingOption
              ? `Até ${selectedShippingOption.delivery_time} dias úteis (${selectedShippingOption.company?.name})`
              : storeDelivery
                ? `${storeDelivery.tempoMin} a ${storeDelivery.tempoMax} minutos`
                : "A combinar"}
          </Typography>
        </Paper>

        {/* Endereço / Modalidade */}
        {deliveryType !== "pickup" && selectedAddress && (
          <Box sx={{ textAlign: "left", mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Endereço de Entrega
            </Typography>
            <Typography variant="body2">
              {selectedAddress.street}, {selectedAddress.house_number}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              {selectedAddress.district}, {selectedAddress.city} - {selectedAddress.state} ({selectedAddress.zip_code})
            </Typography>
          </Box>
        )}

        {deliveryType === "pickup" && (
          <Box sx={{ textAlign: "left", mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
              Forma de Recebimento
            </Typography>
            <Typography variant="body2">Retirada no estabelecimento</Typography>
          </Box>
        )}

        {/* Forma de Pagamento */}
        <Box sx={{ textAlign: "left", mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Forma de Pagamento Selecionada
          </Typography>
          <Typography variant="body2">{paymentLabels[paymentMethod] || "Não informada"}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Resumo de Produtos */}
        <Box sx={{ textAlign: "left", mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Itens do Pedido ({cart.length})
          </Typography>

          {cart.map((item) => {
            const itemBasePrice = Number(item.preco_unitario_momento || 0);
            const optionsPrice = (item.opcoes || []).reduce(
              (optAcc, opt) => optAcc + Number(opt.preco_adicional || 0),
              0
            );
            const unitTotal = itemBasePrice + optionsPrice;
            const itemTotal = unitTotal * Number(item.quantidade || 1);

            return (
              <Box
                key={item.id ?? `${item.nome_momento}-${item.quantidade}`}
                sx={{ mb: 1.5, borderBottom: "1px dashed #eee", pb: 1 }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {item.quantidade}x {item.nome_momento}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {formatCurrency(itemTotal)}
                  </Typography>
                </Box>

                {item.opcoes && item.opcoes.length > 0 && (
                  <Box sx={{ pl: 2, mt: 0.5 }}>
                    {item.opcoes.map((opt) => (
                      <Typography key={opt.id} variant="caption" color="textSecondary" display="block">
                        + {opt.grupo_nome}: {opt.item_nome}{" "}
                        {Number(opt.preco_adicional) > 0 ? `(${formatCurrency(opt.preco_adicional)})` : ""}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Discriminação Detalhada de Valores */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Subtotal dos produtos
          </Typography>
          <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Taxa de entrega {selectedShippingOption ? `(${selectedShippingOption.company?.name})` : ""}
          </Typography>
          <Typography variant="body2">
            {deliveryType === "pickup"
              ? "Isento (Retirada)"
              : shippingFee === 0
                ? "Grátis"
                : formatCurrency(shippingFee)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "1.05rem" }}>
            Valor Total a Pagar
          </Typography>
          <Typography sx={{ fontWeight: "bold", fontSize: "1.15rem", color: themeColor }}>
            {formatCurrency(total)}
          </Typography>
        </Box>
      </Box>
    );
  }
}

/* ------------------------------ Tela de Sucesso ---------------------------- */

class SuccessScreen extends Component {
  static contextType = CartContext;

  render() {
    const { orderId, navigate } = this.props;

    return (
      <Container
        sx={{
          bgcolor: "white",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          maxWidth: "444px !important",
          p: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            border: `3px solid ${SUCCESS_COLOR}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: 50, color: SUCCESS_COLOR }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#1a1a1a", mb: 2 }}>
          Pedido Realizado com Sucesso!
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 4, px: 2 }}>
          Seu pedido foi recebido pelo estabelecimento e já está sendo processado.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          onClick={() => navigate(`/pedidos/${orderId}`)}
          sx={{
            bgcolor: SUCCESS_COLOR,
            "&:hover": { bgcolor: "#4527a0" },
            py: 1.5,
            borderRadius: 2,
            fontWeight: "bold",
            textTransform: "none",
            mb: 2,
          }}
        >
          ACOMPANHAR MEU PEDIDO
        </Button>

        <Button
          fullWidth
          variant="text"
          sx={{
            color: SUCCESS_COLOR,
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { bgcolor: "transparent" },
          }}
          onClick={() => navigate("/")}
        >
          VOLTAR À LOJA
        </Button>
      </Container>
    );
  }
}

/* --------------------------------- Checkout Main -------------------------------- */

class Checkout extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      activeStep: 0,
      loading: true,
      submitting: false,

      cart: [],
      storeId: null,
      cartId: null,
      distanceKm: 0,
      addresses: [],
      paymentsStore: null,
      storeDelivery: null,
      storeAddress: null,
      melhorEnvioOptions: [],

      selectedAddressId: null,
      deliveryType: "delivery",
      selectedShippingOption: null,
      paymentMethod: "",

      orderSuccess: false,
      orderId: null,
    };
  }

  componentDidMount() {
    this.loadCheckoutData();
  }

  loadCheckoutData = async () => {
    try {
      const res = await fetch("/api/store/delivery/checkout", {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();

      if (data.success) {
        const defaultAddress =
          data.address?.find((addr) => addr.default === 1) || data.address?.[0] || null;

        const melhorEnvioOptions = data.melhor_envio_options || [];
        const firstMelhorEnvio = melhorEnvioOptions[0] || null;

        let initialDeliveryType = "delivery";
        if (
          data.store_delivery?.habilitarEntrega === 0 ||
          data.store_delivery?.entregaLocal === 0
        ) {
          if (data.store_delivery?.melhorEnvio === 1 && melhorEnvioOptions.length > 0) {
            initialDeliveryType = "melhor_envio";
          } else if (data.store_delivery?.retiradaLocal === 1) {
            initialDeliveryType = "pickup";
          }
        }

        this.setState({
          storeId: data.store_id,
          cartId: data.cart_id || null,
          cart: data.cart || [],
          distanceKm: Number(data.distance_km || 0),
          addresses: data.address || [],
          paymentsStore: data.paymets_store || null,
          storeDelivery: data.store_delivery || null,
          storeAddress: data.store_address || null,
          melhorEnvioOptions,
          selectedShippingOption: firstMelhorEnvio,
          selectedAddressId: defaultAddress ? defaultAddress.id : null,
          deliveryType: initialDeliveryType,
          loading: false,
        });
      } else {
        toast.error(data.message || "Erro ao carregar dados do checkout.");
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error("Erro ao carregar checkout:", err);
      toast.error("Erro de conexão ao carregar checkout.");
      this.setState({ loading: false });
    }
  };

  validateStep = (step) => {
    const {
      cart,
      deliveryType,
      storeDelivery,
      selectedAddressId,
      addresses,
      distanceKm,
      paymentMethod,
      paymentsStore,
      selectedShippingOption,
    } = this.state;

    if (!cart || cart.length === 0) {
      toast.error("Seu carrinho está vazio.");
      return false;
    }

    if (step === 0) {
      if (deliveryType === "delivery") {
        if (storeDelivery?.habilitarEntrega === 0 || storeDelivery?.entregaLocal === 0) {
          toast.error("O estabelecimento não realiza entregas próprias no momento.");
          return false;
        }

        const selectedAddress = addresses.find((a) => a.id === selectedAddressId);
        if (!selectedAddressId || !selectedAddress) {
          toast.error("Por favor, selecione ou cadastre um endereço de entrega válido.");
          return false;
        }

        if (
          storeDelivery?.distanciaMaxima > 0 &&
          distanceKm > storeDelivery.distanciaMaxima
        ) {
          toast.error(
            `A distância (${distanceKm}km) excede o limite permitido (${storeDelivery.distanciaMaxima}km).`
          );
          return false;
        }
      }

      if (deliveryType === "melhor_envio") {
        if (!selectedShippingOption) {
          toast.error("Por favor, selecione uma opção de frete para continuar.");
          return false;
        }

        const selectedAddress = addresses.find((a) => a.id === selectedAddressId);
        if (!selectedAddressId || !selectedAddress) {
          toast.error("Por favor, selecione ou cadastre um endereço de entrega válido.");
          return false;
        }
      }

      if (deliveryType === "pickup" && storeDelivery?.retiradaLocal === 0) {
        toast.error("O estabelecimento não aceita retirada no local.");
        return false;
      }
    }

    if (step === 1) {
      if (!paymentMethod) {
        toast.error("Por favor, selecione uma forma de pagamento para continuar.");
        return false;
      }

      if (paymentMethod === "credit" && paymentsStore?.credit !== 1) {
        toast.error("Opção de Cartão de Crédito indisponível na loja.");
        return false;
      }
      if (paymentMethod === "debit" && paymentsStore?.debit !== 1) {
        toast.error("Opção de Cartão de Débito indisponível na loja.");
        return false;
      }
      if (paymentMethod === "pix" && paymentsStore?.pix !== 1) {
        toast.error("Opção de Pix indisponível na loja.");
        return false;
      }
      if (paymentMethod === "money" && paymentsStore?.money !== 1) {
        toast.error("Opção de dinheiro na entrega indisponível na loja.");
        return false;
      }
      if (paymentMethod === "machine" && !paymentsStore?.c_machine) {
        toast.error("Opção de maquininha indisponível na loja.");
        return false;
      }
    }

    return true;
  };

  handleNext = async () => {
    const { activeStep, submitting } = this.state;
    if (submitting) return;

    if (!this.validateStep(activeStep)) {
      return;
    }

    // Se estiver no Passo 0 (Entrega), salva a escolha no servidor antes de prosseguir
    if (activeStep === 0) {
      this.setState({ submitting: true });

      const {
        storeId,
        deliveryType,
        selectedAddressId,
        selectedShippingOption,
        cartId,
        cart,
        storeDelivery,
      } = this.state;

      const subtotal = calculateSubtotal(cart);
      const shippingFee = calculateShippingFee(
        deliveryType,
        storeDelivery,
        subtotal,
        selectedShippingOption
      );

      try {
        const res = await fetch("/api/store/delivery/save-delivery-option", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            store_id: storeId,
            cart_id: cartId,
            delivery_type: deliveryType,
            address_id: deliveryType === "pickup" ? null : selectedAddressId,
            shipping_fee: shippingFee,
            shipping_option: deliveryType === "melhor_envio" ? selectedShippingOption : null,
          }),
        });

        const data = await res.json();

        if (data.success) {
          // Avança para o próximo passo se salvou com sucesso
          this.setState((prevState) => ({
            activeStep: prevState.activeStep + 1,
            submitting: false,
          }));
        } else {
          toast.error(data.message || data.error || "Erro ao salvar opção de entrega.");
          this.setState({ submitting: false });
        }
      } catch (err) {
        console.error("Erro ao salvar entrega:", err);
        toast.error("Falha de conexão ao salvar a opção de entrega.");
        this.setState({ submitting: false });
      }

      return;
    }

    // Para os demais passos (ex: Passo 1) apenas avança
    this.setState((prevState) => ({ activeStep: prevState.activeStep + 1 }));
  };

  handleBack = () => {
    this.setState((prevState) => ({
      activeStep: prevState.activeStep === 0 ? 0 : prevState.activeStep - 1,
    }));
  };

  handleOrderConfirmation = async () => {
    const { submitting } = this.state;
    if (submitting) return;

    if (!this.validateStep(0) || !this.validateStep(1)) {
      return;
    }

    this.setState({ submitting: true });

    const {
      storeId,
      deliveryType,
      selectedAddressId,
      paymentMethod,
      cartId,
      cart,
      storeDelivery,
      selectedShippingOption,
    } = this.state;

    const subtotal = calculateSubtotal(cart);
    const shippingFee = calculateShippingFee(
      deliveryType,
      storeDelivery,
      subtotal,
      selectedShippingOption
    );
    const total = subtotal + shippingFee;

    try {
      const res = await fetch("/api/store/delivery/order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          store_id: storeId,
          address_id: deliveryType === "pickup" ? null : selectedAddressId,
          delivery_type: deliveryType,
          payment_method: paymentMethod,
          cart_id: cartId,
          subtotal,
          shipping_fee: shippingFee,
          total,
          shipping_option: deliveryType === "melhor_envio" ? selectedShippingOption : null,
        }),
      });
      const data = await res.json();

      if (data.success) {
        toast.success("Pedido realizado com sucesso!");
        this.setState({ orderId: data.order_id, orderSuccess: true, submitting: false });
      } else {
        toast.error(data.error || "Erro ao finalizar o pedido. Tente novamente.");
        this.setState({ submitting: false });
      }
    } catch (err) {
      console.error("Erro no pedido:", err);
      toast.error("Falha na comunicação com o servidor ao enviar o pedido.");
      this.setState({ submitting: false });
    }
  };

  render() {
    const { navigate } = this.props;
    const themeColor = this.context?.data?.color || this.context?.color || DEFAULT_COLOR;

    const {
      activeStep,
      loading,
      submitting,
      cart,
      distanceKm,
      addresses,
      paymentsStore,
      storeDelivery,
      storeAddress,
      melhorEnvioOptions,
      selectedShippingOption,
      selectedAddressId,
      deliveryType,
      paymentMethod,
      orderSuccess,
      orderId,
    } = this.state;

    if (loading) {
      return (
        <Container sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
          <CircularProgress sx={{ color: themeColor }} />
        </Container>
      );
    }

    if (orderSuccess) {
      return <SuccessScreen orderId={orderId} navigate={navigate} />;
    }

    const subtotal = calculateSubtotal(cart);
    const shippingFee = calculateShippingFee(
      deliveryType,
      storeDelivery,
      subtotal,
      selectedShippingOption
    );
    const total = subtotal + shippingFee;
    const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId) || addresses[0];

    return (
      <Container
        sx={{
          bgcolor: "white",
          minHeight: "100vh",
          p: 0,
          display: "flex",
          flexDirection: "column",
          maxWidth: "444px !important",
        }}
      >
        {/* Navbar Header */}
        <Box sx={{ display: "flex", alignItems: "center", p: 2, borderBottom: "1px solid #eee" }}>
          <IconButton onClick={this.handleBack} sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: "center", fontWeight: "bold" }}>
            Finalizar Pedido
          </Typography>
          <IconButton onClick={() => navigate(-1)}>
            <Close />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Box sx={{ py: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-active, &.Mui-completed": { color: themeColor },
                      "& .MuiStepIcon-text": { fill: "white" },
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: activeStep >= index ? themeColor : "#999", fontWeight: "bold" }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Conteúdo do Passo Atual */}
        <Box sx={{ flexGrow: 1, px: 2, pb: 12 }}>
          {activeStep === 0 && (
            <DeliveryStep
              storeDelivery={storeDelivery}
              deliveryType={deliveryType}
              onSelectDeliveryType={(type) => this.setState({ deliveryType: type })}
              selectedAddress={selectedAddress}
              shippingFee={shippingFee}
              distanceKm={distanceKm}
              melhorEnvioOptions={melhorEnvioOptions}
              selectedShippingOption={selectedShippingOption}
              onSelectShippingOption={(option) => this.setState({ selectedShippingOption: option })}
            />
          )}

          {activeStep === 1 && (
            <PaymentStep
              paymentsStore={paymentsStore}
              paymentMethod={paymentMethod}
              onSelectPaymentMethod={(method) => this.setState({ paymentMethod: method })}
            />
          )}

          {activeStep === 2 && (
            <ConfirmationStep
              storeDelivery={storeDelivery}
              storeAddress={storeAddress}
              deliveryType={deliveryType}
              selectedAddress={selectedAddress}
              cart={cart}
              subtotal={subtotal}
              shippingFee={shippingFee}
              total={total}
              paymentMethod={paymentMethod}
              selectedShippingOption={selectedShippingOption}
            />
          )}
        </Box>

        {/* Botão Fixo Inferior */}
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            width: "100%",
            maxWidth: "444px",
            p: 2,
            bgcolor: "white",
            borderTop: "1px solid #eee",
            zIndex: 1000,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: themeColor,
              "&:hover": { bgcolor: themeColor },
              py: 1.5,
              borderRadius: 2,
              fontWeight: "bold",
              fontSize: "1rem",
            }}
            onClick={activeStep === 2 ? this.handleOrderConfirmation : this.handleNext}
          >
            {submitting ? (
              <CircularProgress size={22} sx={{ color: "white" }} />
            ) : activeStep === 2 ? (
              `CONFIRMAR E PAGAR ${formatCurrency(total)}`
            ) : (
              "CONTINUAR"
            )}
          </Button>
        </Box>
      </Container>
    );
  }
}

export default WithRouter(Checkout);